import { History } from 'history'
import usePrevious from 'lib/hooks/usePrevious'
import createDebug from 'lib/util/createDebug'
import { QUERY_KEYS } from 'lib/util/routing'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDebounce } from 'use-debounce'

const debug = createDebug('useSearchInput')

function updateUrl(history: History, newSearch: string) {
  const params = new URLSearchParams(history.location.search)
  if (newSearch) {
    params.set(QUERY_KEYS.search, newSearch)
  } else {
    params.delete(QUERY_KEYS.search)
  }
  history.replace({ ...history.location, search: `?${params.toString()}`, state: history.location.state })
}

export default function useSearchInput() {
  const history = useHistory()
  const [searchInput, setSearchInput] = useState(
    new URLSearchParams(history.location.search).get(QUERY_KEYS.search) || ''
  )
  const [debouncedSearchInput] = useDebounce(searchInput, 200)
  const previousQueryParams = usePrevious(history.location.search)

  const clearSearchInput = () => setSearchInput('')

  useEffect(() => {
    debug(`Updating URL [${debouncedSearchInput}]`)
    updateUrl(history, debouncedSearchInput)
  }, [debouncedSearchInput, history])

  useEffect(() => {
    const prevSearchParams = new URLSearchParams(previousQueryParams)
    const searchParams = new URLSearchParams(history.location.search)
    const searchFromUrl = searchParams.get(QUERY_KEYS.search)
    prevSearchParams.delete(QUERY_KEYS.search)
    searchParams.delete(QUERY_KEYS.search)
    if (prevSearchParams.toString() !== searchParams.toString()) {
      // If the location changes (excluding the `search` query parameter which gets updated when a user uses the search input),
      // restore the search input value from the URL. This is needed since the Upcoming/RecordedWebinars/Videos Section components
      // do not get remounted each time the location changes.
      // For example, when a user clicks a series link in the `ListItemHeader` component from a search, we need to
      // clear the search input manually like this from the URL. Then, if the back button is clicked, we need to restore it.
      setSearchInput(searchFromUrl || '')
    }
  }, [history.location.search, previousQueryParams])

  return { searchInput, setSearchInput, clearSearchInput }
}
